import React, { useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, Input, NoticeBox } from "../../ui";
import { createChangeRequestAPI, editChangeRequestAPI } from "../../../utils/api/applications";
import { errorMessage } from "./applicationsText";

// Small modal to add or update ONE branch's account in ONE application: the
// application id and name (store.branches[].apps[app]). It is sent as a
// "mapping" request, so the live data changes only after an administrator
// approves; the branch's status is never touched. `request` edits a pending one.
export default function BranchAccountModal({ open, storeId, app, appName, branch, initial = {}, request = null, t, token, onClose, onDone }) {
  const [values, setValues] = useState({ appId: "", appName: "" });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const seed = request?.proposed?.branches?.[0] || initial;
  useEffect(() => {
    if (!open) return;
    setValues({ appId: seed.appId || "", appName: seed.appName || "" });
    setErrors({});
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, app?.key, branch?.id, seed.appId, seed.appName]);

  if (!open || !app || !branch) return null;
  const copy = t.branchAccounts;
  const editing = Boolean(request) || Boolean(initial.appId || initial.appName);

  const submit = async (event) => {
    event?.preventDefault();
    const found = {};
    if (!values.appId.trim()) found.appId = "required";
    if (!values.appName.trim()) found.appName = "required";
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setError("");
    const payload = { branches: [{ branchId: branch.id, appId: values.appId.trim(), appName: values.appName.trim() }] };
    try {
      if (request) await editChangeRequestAPI(token, storeId, request.id, { version: request.version, ...payload });
      else await createChangeRequestAPI(token, storeId, app.key, { type: "mapping", ...payload });
      onDone?.(request ? t.flow.requestUpdated : t.flow.requestSent, "success");
    } catch (e) {
      if (!alive.current) return;
      const server = e.data?.fields || {};
      setErrors({ appId: server["branches.0.appId"] ? (server["branches.0.appId"] === "DUPLICATE" ? "duplicate" : "required") : undefined, appName: server["branches.0.appName"] ? "required" : undefined });
      setError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const message = (code) => (code ? (code === "duplicate" ? copy.duplicate : t.integrate[code] || t.integrate.invalid) : undefined);

  return (
    <LudDashboardModal
      open
      size="sm"
      title={`${editing ? copy.editTitle(appName) : copy.addTitle(appName)} — ${branch.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="neutral" appearance="soft" onClick={onClose} disabled={busy}>{t.cancel}</Button>
          <Button variant="primary" appearance="filled" onClick={submit} disabled={busy} loading={busy}>{copy.submit}</Button>
        </>
      }
    >
      <form className="lud-app-form" onSubmit={submit} noValidate>
        {error && <NoticeBox variant="danger" size="sm">{error}</NoticeBox>}
        <Input label={copy.fieldId} dir="ltr" value={values.appId} onChange={(event) => setValues({ ...values, appId: event.target.value })} error={message(errors.appId)} required autoFocus />
        <Input label={copy.fieldName} value={values.appName} onChange={(event) => setValues({ ...values, appName: event.target.value })} error={message(errors.appName)} required />
        <small className="lud-app-hint">{copy.modalNote}</small>
      </form>
    </LudDashboardModal>
  );
}
